import React, { useEffect, useState } from "react";
import { Flag } from "lucide-react";
import { ujmsV2Summary } from "../../services/ujmsApi";
import { KV, SectionHdr, EventsHearings, useSummaryFeeds, usDate } from "./SummaryTab";
import { SummaryWidgets } from "./SummaryWidgets";
import { TrialPrepPanels } from "./TrialPrepPanels";
import { ChargeAmendments } from "./charges/ChargeAmendments";
import { can } from "../ui";

const PROSECUTION = ["Prosecutor", "State's Attorney"];

export function AttorneySummaryTab({ shell, setTab, access, reload }) {
  const c = shell.case;
  const { charges, events, hearings } = useSummaryFeeds(c.id);
  const [summary, setSummary] = useState(null);
  useEffect(() => { ujmsV2Summary(c.id).then((r) => setSummary(r.data)).catch(() => setSummary({})); }, [c.id]);
  const p = shell.primary_party;
  const d = summary?.demographics;
  const side = (access?.ujms_roles || []).some((r) => PROSECUTION.includes(r)) ? "PROSECUTION" : "DEFENSE";
  const state = shell.parties.find((x) => (x.relationship_type || "").toLowerCase() === "state" || (x.party_type || "") === "Government Agency");
  const prosecutor = (summary?.assignments || []).find((a) => a.active && /PROSECUTOR|ASA|LEAD/i.test(a.role))?.assigneeName || state?.attorney_name;
  const defense = shell.parties.find((x) => x.person_id && x.attorney_name)?.attorney_name;
  const origin = c.case_origin || c.origin || (c.legacy_case_id ? "Transfer" : c.case_type === "CRIMINAL" ? "Arrest" : "Filing");
  return (
    <div className="space-y-1.5" data-testid="v2-attorney-summary" data-side={side}>
      <div className="grid grid-cols-[1fr_auto] gap-4 px-1 pb-1">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-amber-700 h-4">{shell.flags.slice(0, 6).map((f) => <Flag key={f} className="w-3 h-3 fill-amber-400" title={f} />)}</div>
          <div className="text-[13px] font-bold text-slate-900 leading-4" data-testid="v2-att-party">{p?.display_name || c.caption}</div>
          <div className="text-[12px] text-slate-700 leading-4 flex flex-wrap gap-x-2">
            {d?.race != null && d?.race && <span>{d.race}</span>}{d?.sex && <span>{d.sex}</span>}
            <span><span className="font-semibold">DOB</span> {d?.dob ? usDate(d.dob) : "—"}</span>
          </div>
          <div className="text-[12px] text-slate-700 leading-4 flex flex-wrap gap-x-3">
            <span><span className="font-semibold">DL</span> {d?.driver_license || "—"}</span><span><span className="font-semibold">SID</span> {d?.state_id || "—"}</span>
          </div>
        </div>
        <KV testIdPrefix="v2-att" rows={[["Status", <span className="text-ujms-deep">{c.status}</span>], ["Case Type", c.case_type], ["Case Origin", origin], ["Prosecutor", prosecutor], ...(side === "DEFENSE" ? [["Def. Attorney", defense]] : [])]} />
      </div>
      {can(access, "charge.amend.propose", "charge.amend.approve") && <ChargeAmendments caseId={c.id} />}
      <section className="ent-panel" data-testid="v2-att-charges">
        <SectionHdr title="Charges" />
        <table className="w-full text-[12px]">
          <thead><tr className="ent-th"><th className="px-1.5 py-px text-left">Description</th><th className="px-1.5 text-left w-28">Status</th><th className="px-1.5 text-left w-36">Case #</th><th className="px-1.5 text-left w-40">Decision</th><th className="px-1.5 text-left w-40">Def. Attorney</th></tr></thead>
          <tbody>
            {charges === null && <tr><td colSpan={5} className="text-slate-400 px-2 py-1">Loading…</td></tr>}
            {charges?.length === 0 && <tr><td colSpan={5} className="text-slate-400 italic px-2 py-1">No charges filed.</td></tr>}
            {(charges || []).map((ch) => (
              <tr key={ch.id} className="border-b border-slate-100 hover:bg-ujms-soft/60 cursor-pointer align-top" onClick={() => setTab?.("Charges")} data-testid={`v2-att-charge-${ch.count_number}`}>
                <td className="px-1.5 py-[2px]"><div>{ch.count_number}. <span className="tyler-charge-link uppercase">{ch.description}</span></div><div className="text-[11px] text-slate-600 pl-4">{usDate(ch.filed_date)} ({ch.classification}) {ch.statute_code}</div></td>
                <td className="px-1.5 py-[2px]">{(ch.status || "").replace(/_/g, " ")}</td>
                <td className="px-1.5 py-[2px] font-semibold text-slate-800">{c.case_number}</td>
                <td className="px-1.5 py-[2px]">{ch.disposition ? `${ch.disposition}${ch.disposition_date ? ` ${usDate(ch.disposition_date)}` : ""}` : ch.status === "FILED" ? "Filed with Clerk" : "—"}</td>
                <td className="px-1.5 py-[2px]">{ch.defense_attorney || defense || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <TrialPrepPanels caseId={c.id} />
      <div className="grid grid-cols-1 xl:grid-cols-[68fr_32fr] gap-1.5 items-start">
        <EventsHearings caseId={c.id} events={events} hearings={hearings} judge={shell.judge} />
        <SummaryWidgets shell={shell} access={access} onChanged={reload} include={["assignments", "flags", "related"]} />
      </div>
    </div>
  );
}
